/*
 * Everything the client profile's chrome and sections share: the client
 * record, URL-driven section state, nav visibility, cases, badges and the
 * create-case flow. Composed once in `ClientProfilePage`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import type { ForeignNational } from "../../../api/types/foreignNational";
import type { MatterDTO } from "../../../api/types/matter";
import { useForeignNationals } from "../../foreignNationals/context/ForeignNationalsContext";
import {
  useClientAttentionBadges,
  type AttentionBadges
} from "../hooks/useClientAttentionBadges";
import {
  useClientMatters,
  type ClientMatters
} from "../hooks/useClientMatters";
import {
  useClientNavState,
  type ClientNavState
} from "../hooks/useClientNavState";
import {
  useClientProfileUrlState,
  type ClientProfileUrlApi
} from "../hooks/useClientProfileUrlState";
import {
  useLawFirmWhitelabel,
  type LawFirmWhitelabel
} from "../hooks/useLawFirmWhitelabel";
import { useOnClientInvalidate } from "../lib/clientInvalidation";
import { scheduleScrollToDomId } from "../lib/scrollToDomId";
import type { ClientTab } from "../urlState";

export type CreateCaseOptions = {
  /** Prefill from an existing case (onboarding's "change case type"). */
  prefillMatter?: MatterDTO | null;
  /** Case to delete once the new one exists. */
  replaceMatter?: MatterDTO | null;
  /** Land on Overview instead of Cases afterwards. */
  returnToOverview?: boolean;
};

export type CreateCaseFlow = {
  isOpen: boolean;
  prefillMatter: MatterDTO | null;
  open: (opts?: CreateCaseOptions) => void;
  setOpen: (open: boolean) => void;
  onCreated: (created: MatterDTO) => void;
};

export type DeleteClientFlow = {
  isOpen: boolean;
  open: () => void;
  setOpen: (open: boolean) => void;
};

export type ClientProfileContextValue = {
  clientId: string;
  client: ForeignNational | null;
  isLoadingClient: boolean;
  clientError: Error | null;
  reloadClient: () => Promise<void>;
  url: ClientProfileUrlApi;
  activeTab: ClientTab;
  /** True on the Requests tab, where the Requests IDE takes the rail's width. */
  inIntakeFlow: boolean;
  nav: ClientNavState;
  matters: ClientMatters;
  badges: AttentionBadges;
  whitelabel: LawFirmWhitelabel;
  createCase: CreateCaseFlow;
  deleteClient: DeleteClientFlow;
  /** Jump to Personal Information, optionally scrolling to a section id. */
  openPersonalSection: (sectionId?: string) => void;
  /** Whether Overview should show the onboarding checklist. */
  onboarding: { pending: boolean; show: boolean };
};

const Ctx = createContext<ClientProfileContextValue | null>(null);

export const useClientProfile = (): ClientProfileContextValue => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useClientProfile must be used within ClientProfileProvider"
    );
  }
  return ctx;
};

/** Re-provide a profile to controls the page hands to the app tab bar, which sits outside the provider. */
export const ClientProfileBridge: FC<{
  value: ClientProfileContextValue;
  children: ReactNode;
}> = ({ value, children }) => (
  <Ctx.Provider value={value}>{children}</Ctx.Provider>
);

export const ClientProfileProvider: FC<{
  clientId: string;
  children: ReactNode;
}> = ({ clientId, children }) => {
  const {
    selectedForeignNational,
    fetchForeignNational,
    isLoadingDetail,
    error
  } = useForeignNationals();

  const url = useClientProfileUrlState();
  const activeTab = url.state.tab;
  const nav = useClientNavState(activeTab, clientId);
  const matters = useClientMatters(clientId);
  const badges = useClientAttentionBadges(clientId);

  const client = useMemo(
    () =>
      selectedForeignNational?.uid === clientId
        ? selectedForeignNational
        : null,
    [clientId, selectedForeignNational]
  );
  const whitelabel = useLawFirmWhitelabel(client);

  const reloadClient = useCallback(
    () => fetchForeignNational(clientId),
    [clientId, fetchForeignNational]
  );

  useEffect(() => {
    reloadClient().catch(() => {
      // Surfaced through `error`.
    });
  }, [reloadClient]);

  // Re-read badges whenever the section changes (mutations happen inside them).
  const { refresh: refreshBadges } = badges;
  const isFirstTabRef = useRef(true);
  useEffect(() => {
    if (isFirstTabRef.current) {
      isFirstTabRef.current = false; // the hook already fetched on mount
      return;
    }
    refreshBadges();
  }, [activeTab, refreshBadges]);

  // The client manager agent (in the global panel) reports what it changed;
  // refresh the shared pieces here, and let each tab refresh its own list.
  const { refresh: refreshMatters } = matters;
  useOnClientInvalidate(
    clientId,
    ["client", "cases", "reminders", "intakes", "records"],
    (hit) => {
      if (hit.includes("client")) {
        reloadClient().catch(() => {
          // Surfaced through `error`.
        });
      }
      if (hit.includes("cases")) void refreshMatters();
      // Badges count reminders, intakes and missing profile fields.
      refreshBadges();
    }
  );

  // The Requests tab is the Requests IDE (explorer | tabs | Client Manager);
  // it takes the rail's width, as the old intake wizard did.
  const inIntakeFlow = activeTab === "outgoing_intakes";

  // Create-case flow -------------------------------------------------------
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [prefillMatter, setPrefillMatter] = useState<MatterDTO | null>(null);
  const [replaceMatter, setReplaceMatter] = useState<MatterDTO | null>(null);
  const [returnToOverview, setReturnToOverview] = useState(false);

  const openCreateCase = useCallback((opts?: CreateCaseOptions) => {
    setPrefillMatter(opts?.prefillMatter ?? null);
    setReplaceMatter(opts?.replaceMatter ?? null);
    setReturnToOverview(Boolean(opts?.returnToOverview));
    setIsCreateOpen(true);
  }, []);

  const setCreateOpen = useCallback((open: boolean) => {
    setIsCreateOpen(open);
    if (!open) {
      setPrefillMatter(null);
      setReplaceMatter(null);
    }
  }, []);

  const { applyCreated } = matters;
  const { setTab } = url;
  const onCreated = useCallback(
    (created: MatterDTO) => {
      applyCreated(created, replaceMatter);
      setPrefillMatter(null);
      setReplaceMatter(null);
      setIsCreateOpen(false);
      setTab(returnToOverview ? "overview" : "cases");
      setReturnToOverview(false);
    },
    [applyCreated, replaceMatter, returnToOverview, setTab]
  );

  const createCase = useMemo<CreateCaseFlow>(
    () => ({
      isOpen: isCreateOpen,
      prefillMatter,
      open: openCreateCase,
      setOpen: setCreateOpen,
      onCreated
    }),
    [isCreateOpen, onCreated, openCreateCase, prefillMatter, setCreateOpen]
  );

  const wantsNewCase = Boolean(url.state.focus.newCase);
  useEffect(() => {
    if (!wantsNewCase) return;
    setTab("cases");
    openCreateCase();
  }, [openCreateCase, setTab, wantsNewCase]);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const deleteClient = useMemo<DeleteClientFlow>(
    () => ({
      isOpen: isDeleteOpen,
      open: () => setIsDeleteOpen(true),
      setOpen: setIsDeleteOpen
    }),
    [isDeleteOpen]
  );

  const openPersonalSection = useCallback(
    (sectionId?: string) => {
      setTab("personal");
      if (sectionId) scheduleScrollToDomId(sectionId);
    },
    [setTab]
  );

  const onboarding = useMemo(() => {
    if (activeTab !== "overview") return { pending: false, show: false };
    const casesCount = matters.matters.length;
    const pending =
      casesCount <= 1 &&
      (badges.isLoadingIntakesCount || typeof badges.intakesCount !== "number");
    const show =
      !pending &&
      typeof badges.intakesCount === "number" &&
      casesCount <= 1 &&
      badges.intakesCount < 1;
    return { pending, show };
  }, [
    activeTab,
    badges.intakesCount,
    badges.isLoadingIntakesCount,
    matters.matters.length
  ]);

  const value = useMemo<ClientProfileContextValue>(
    () => ({
      clientId,
      client,
      isLoadingClient: isLoadingDetail,
      clientError: error,
      reloadClient,
      url,
      activeTab,
      inIntakeFlow,
      nav,
      matters,
      badges,
      whitelabel,
      createCase,
      deleteClient,
      openPersonalSection,
      onboarding
    }),
    [
      activeTab,
      badges,
      client,
      clientId,
      createCase,
      deleteClient,
      error,
      inIntakeFlow,
      isLoadingDetail,
      matters,
      nav,
      onboarding,
      openPersonalSection,
      reloadClient,
      url,
      whitelabel
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
